/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import './style/dvrdDatePicker.scss';

import classNames from 'classnames';
import React, {
    ForwardedRef,
    forwardRef,
    Fragment,
    MouseEventHandler,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';
import {ChangeFunction, DatePickerTimeMode, ErrorType} from '../util/interfaces';
import AwesomeIcon from '../icon/awesomeIcon';
import WithBackground from '../popup/withBackground';
import DvrdButton from "../button/dvrdButton";
import {generateComponentId} from "../util/componentUtil";
import IDate from '@dvrd/idate';
import {pad} from "../util/controlUtil";
import defer from 'lodash.defer';
import {Swiper, SwiperClass, SwiperSlide} from 'swiper/react';
import {FreeMode, Mousewheel} from 'swiper/modules';
import 'swiper/css';

interface Props {
    onChange: ChangeFunction<IDate>;
    closeOnChange?: boolean;
    value: IDate | null;
    label?: string;
    timeMode?: DatePickerTimeMode;
    error?: ErrorType;
    className?: string;
    placeholder?: string;
    dateFormat?: string;
    disabled?: boolean;
    alwaysShowArrows?: boolean;
    useMobileNative?: boolean;
    id?: string;
    max?: IDate | string;
    min?: IDate | string;
    pickersOnly?: boolean;
}

export type DVRDDatePickerRef = { onOpen: VoidFunction; onClose: VoidFunction; }

function DvrdDatePicker(props: Props, ref: ForwardedRef<DVRDDatePickerRef>) {
    const {
        onChange, className, closeOnChange, error, label, value, placeholder, disabled, alwaysShowArrows,
        useMobileNative, max, min, timeMode, pickersOnly
    } = props;
    const [pickerOpen, setPickerOpen] = useState(false);
    const [timePickerOpen, setTimePickerOpen] = useState(false);
    const dateFormat = useMemo(() => {
        if (props.dateFormat) return props.dateFormat;
        let format = 'YYYY-MM-DD';
        if (timeMode === DatePickerTimeMode.FULL) format += ' HH:mm:ss';
        else if (timeMode === DatePickerTimeMode.HOURS_MINUTES) format += ' HH:mm';
        else if (timeMode === DatePickerTimeMode.HOURS_ONLY) format += ' HH';
        else if (timeMode === DatePickerTimeMode.MINUTES_ONLY) format += ' mm';
        else if (timeMode === DatePickerTimeMode.SECONDS_ONLY) format += ' ss';
        return format;
    }, [props.dateFormat, timeMode]);
    const id = useMemo(() => {
        return generateComponentId(props.id);
    }, [props.id]);

    function onChangeNative(evt: React.ChangeEvent<HTMLInputElement>) {
        onChange(new IDate(evt.target.value), evt);
    }

    function onClickContainer() {
        if (!disabled)
            setPickerOpen(true);
    }

    const onChangePicker = (daySelected: boolean) => (value: IDate) => {
        onChange(value.clone());
        if (closeOnChange && daySelected) {
            onClosePicker();
            if (!!timeMode) setTimePickerOpen(true);
        }
    };

    function onChangeTimePicker(value: IDate) {
        onChange(value.clone());
        setTimePickerOpen(false);
    }

    function onClosePicker() {
        setPickerOpen(false);
    }

    function renderMobilePicker() {
        if (!useMobileNative) return null;
        return (
            <div className='native-picker-container'>
                <input type='date' onChange={onChangeNative} value={value?.format('YYYY-MM-DD') ?? ''}
                       placeholder={placeholder} className='native-date-field' id={`${id}-native`}/>
                <AwesomeIcon name='calendar-alt' className='calendar-icon' htmlFor={`${id}-native`}/>
            </div>
        )
    }

    useImperativeHandle(ref, () => ({onOpen: onClickContainer, onClose: onClosePicker}));

    return (
        <div className={classNames('dvrd-date-picker', className, error && 'error', disabled && 'disabled')}>
            {!pickersOnly && <>
                {!!label && <label className='field-label'>{label}</label>}
                {renderMobilePicker()}
                <div className={classNames('value-container', useMobileNative && 'no-mob')} onClick={onClickContainer}>
                    <label className={classNames('value', !value && 'placeholder')}>{value?.format(dateFormat) ??
                        placeholder ?? ''}</label>
                    <AwesomeIcon name='calendar-alt' className='calendar-icon'/>
                </div>
            </>}
            <DatePicker onClose={onClosePicker} onChange={onChangePicker} open={pickerOpen} value={value}
                        alwaysShowArrows={alwaysShowArrows} max={max} min={min}/>
            {!!timeMode &&
                <TimePicker onChange={onChangeTimePicker} onClose={onClosePicker} value={value}
                            open={timePickerOpen} timeMode={timeMode} closeOnChange={closeOnChange}/>}
            {!pickersOnly && <label className='error-label'>{error}</label>}
        </div>
    )
}

interface DatePickerProps {
    onChange: (daySelected: boolean) => ChangeFunction<IDate>;
    onClose: VoidFunction;
    value: IDate | null;
    open: boolean;
    alwaysShowArrows?: boolean;
    max?: IDate | string;
    min?: IDate | string;
}

function DatePicker(props: DatePickerProps) {
    const {min, max, alwaysShowArrows, open, onClose, value, onChange} = props;
    const divRef = useRef<HTMLDivElement>(null);
    const pickerValue = useMemo(() => new IDate(value), [value]);
    const [padBeforeDays, days, padAfterDays] = useMemo(() => {
        const daysInMonth = pickerValue.daysInMonth();
        const days: number[] = [];
        const padBeforeDays: number[] = [];
        const padAfterDays: number[] = [];
        for (let i = 1; i <= daysInMonth; i++) days.push(i);
        let firstMonthDay = pickerValue.clone().day(1).weekday();
        if (!firstMonthDay) firstMonthDay = 7;
        const padDaysBefore = firstMonthDay - 1;
        let dayBefore = pickerValue.clone().add(-1, 'month').daysInMonth()
        for (let i = 0; i < padDaysBefore; i++)
            padBeforeDays.splice(0, 0, dayBefore--);

        const lastWeekDay = pickerValue.clone().day(daysInMonth).weekday();
        const padDaysAfter = 7 - lastWeekDay;
        for (let i = 0; i < padDaysAfter; i++)
            padAfterDays.push(i + 1);

        if (Math.ceil(padAfterDays.concat(days).concat(padBeforeDays).length / 7) < 6) {
            let dayAfter = padAfterDays[padAfterDays.length - 1] + 1;
            for (let i = 0; i < 7; i++)
                padAfterDays.push(dayAfter++);
        }
        return [padBeforeDays, days, padAfterDays];
    }, [pickerValue]);

    function onReduce(key: 'year' | 'month') {
        return function () {
            const value = pickerValue.clone();
            value.add(-1, key);
            value.day(Math.min(value.daysInMonth(), value.day()))
            onChange(false)(value);
        }
    }

    function onAdd(key: 'year' | 'month') {
        return function () {
            const value = pickerValue.clone();
            value.add(1, key);
            value.day(Math.min(value.daysInMonth(), value.day()))
            onChange(false)(value);
        }
    }

    function onResetSwitcher(key: 'year' | 'month') {
        return function () {
            const value = pickerValue.clone();
            if (key === 'year') value.year(IDate.now().year());
            else value.month(IDate.now().month());
            value.day(Math.min(value.daysInMonth(), value.day()))
            onChange(false)(value);
        }
    }

    function onClickToday() {
        onChange(true)(IDate.now());
    }

    function onSelectDay(day: number, month?: number, year?: number) {
        return function () {
            const pickValue = pickerValue.clone();
            if (day || value === null) {
                pickValue.day(day);
                if (month !== undefined) pickValue.month(month);
                if (year !== undefined) pickValue.year(year);
                onChange(true)(pickValue);
            }
        }
    }

    function onSelectPadDay(day: number, kind: 'prev' | 'next') {
        return function () {
            const value = pickerValue.clone();
            if (kind === 'prev') value.add(-1, 'month');
            else value.add(1, 'month');
            value.day(day);
            onChange(true)(value);
        }
    }

    function keyListener(evt: KeyboardEvent) {
        const {key, shiftKey, ctrlKey} = evt;
        if (['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Enter'].includes(key)) {
            evt.preventDefault();
        }
        if (key === 'ArrowLeft') onLeftKey(shiftKey, ctrlKey);
        else if (key === 'ArrowRight') onRightKey(shiftKey, ctrlKey);
        else if (key === 'ArrowUp') onUpKey();
        else if (key === 'ArrowDown') onDownKey();
        else if (key === 'Enter') onClose();
    }

    function onLeftKey(shiftKey: boolean, ctrlKey: boolean) {
        const value = pickerValue.clone();
        if (shiftKey) {
            if (ctrlKey) onReduce('year')();
            else onReduce('month')();
        } else onChange(false)(value.add(-1, 'days'));
    }

    function onRightKey(shiftKey: boolean, ctrlKey: boolean) {
        const value = pickerValue.clone();
        if (shiftKey) {
            if (ctrlKey) onAdd('year')();
            else onAdd('month')();
        } else onChange(false)(value.add(1, 'days'));
    }

    function onUpKey() {
        const value = pickerValue.clone();
        onChange(false)(value.add(-7, 'days'));
    }

    function onDownKey() {
        const value = pickerValue.clone();
        onChange(false)(value.add(7, 'days'));
    }

    function addMountClass() {
        const div = divRef.current;
        if (div?.classList.contains('switch-mount'))
            div.classList.add('switch-mount');
    }

    function removeMountClass() {
        if (!alwaysShowArrows)
            divRef.current?.classList.remove('switch-mount');
    }

    function dateIsDisabled(day: number, month?: number, year?: number) {
        if (!min && !max) return false;
        const pickValue = pickerValue.clone();
        pickValue.day(day);
        if (month !== undefined) pickValue.month(month);
        if (year !== undefined) pickValue.year(year);
        if (min && new IDate(min).isAfter(pickValue, 'day')) return true;
        else if (max && new IDate(max).isBefore(pickValue, 'day')) return true;
        return false;
    }

    function renderSwitcher(key: 'year' | 'month', kind: 'add' | 'reduce') {
        const icon = kind === 'add' ? 'chevron-right' : 'chevron-left';
        const title = kind === 'add' ? 'Volgende' : 'Vorige';
        const onClick = kind === 'add' ? onAdd(key) : onReduce(key);
        return <AwesomeIcon name={icon} title={title} className={classNames('switcher-icon', kind)} onClick={onClick}/>
    }

    function renderTooltipContainer() {
        return (
            <div className='tooltip-container'>
                <AwesomeIcon name='question-circle' className='tooltip-icon' prefix='far'/>
                <div className='tooltip'>
                    <label className='tooltip-label'>Shortcuts</label>
                    <div className='key'>
                        <AwesomeIcon name='arrow-left' className='shortcut'/>
                        <span className='directive'>-1 dag</span>
                    </div>
                    <div className='key'>
                        <div className='directives'>
                            <span className='shortcut text'>SHIFT</span>
                            <span className='directive'>+</span>
                            <AwesomeIcon name='arrow-left' className='shortcut'/>
                        </div>
                        <span className='directive'>-1 maand</span>
                    </div>
                    <div className='key'>
                        <div className='directives full'>
                            <span className='shortcut text'>CTRL</span>
                            <span className='directive'>+</span>
                            <span className='shortcut text'>SHIFT</span>
                            <span className='directive'>+</span>
                            <AwesomeIcon name='arrow-left' className='shortcut'/>
                        </div>
                        <span className='directive'>-1 jaar</span>
                    </div>
                    <div className='key'>
                        <AwesomeIcon name='arrow-right' className='shortcut'/>
                        <span className='directive'>+1 dag</span>
                    </div>
                    <div className='key'>
                        <div className='directives'>
                            <span className='shortcut text'>SHIFT</span>
                            <span className='directive'>+</span>
                            <AwesomeIcon name='arrow-right' className='shortcut'/>
                        </div>
                        <span className='directive'>+1 maand</span>
                    </div>
                    <div className='key'>
                        <div className='directives full'>
                            <span className='shortcut text'>CTRL</span>
                            <span className='directive'>+</span>
                            <span className='shortcut text'>SHIFT</span>
                            <span className='directive'>+</span>
                            <AwesomeIcon name='arrow-right' className='shortcut'/>
                        </div>
                        <span className='directive'>+1 jaar</span>
                    </div>
                    <div className='key'>
                        <AwesomeIcon name='arrow-up' className='shortcut'/>
                        <span className='directive'>-7 dagen</span>
                    </div>
                    <div className='key'>
                        <AwesomeIcon name='arrow-down' className='shortcut'/>
                        <span className='directive'>+7 dagen</span>
                    </div>
                </div>
            </div>
        )
    }

    useEffect(() => {
        if (open) {
            document.addEventListener('keydown', keyListener);
            defer(addMountClass);
            window.setTimeout(removeMountClass, 1000);
        } else {
            document.removeEventListener('keydown', keyListener);
        }
        return function () {
            document.removeEventListener('keydown', keyListener);
        }
    }, [open]);

    function renderDay(day: number, index: number, onClick: MouseEventHandler, className?: string,
                       type?: 'prev' | 'next') {
        let pickValue = pickerValue.clone();
        if (type === 'prev')
            pickValue = pickValue.subtract(1, 'month');
        else if (type === 'next')
            pickValue = pickValue.add(1, 'month');
        const month = pickValue.month();
        const year = pickValue.year();
        const disabled = dateIsDisabled(day, month, year);
        return (
            <div className={classNames('day', disabled && 'disabled', className)}
                 onClick={disabled ? undefined : onClick} key={index}>
                <span className='day-label'>{day}</span>
            </div>
        )
    }

    return (
        <WithBackground active={open} onClose={onClose}>
            <div className={classNames('picker', alwaysShowArrows && 'switch-mount')} ref={divRef}>
                <div className='switcher year'>
                    {renderSwitcher('year', 'reduce')}
                    <label className='switcher-label'
                           onClick={onResetSwitcher('year')}>{pickerValue.year()}</label>
                    {renderSwitcher('year', 'add')}
                </div>
                <div className='switcher month'>
                    {renderSwitcher('month', 'reduce')}
                    <label className='switcher-label' onClick={onResetSwitcher('month')}>{pickerValue.format(
                        'MMMM')}</label>
                    {renderSwitcher('month', 'add')}
                </div>
                <div className='current-date-container'>
                    <label className='current-date-label'>{pickerValue.format('dddd D MMMM YYYY')}</label>
                    {renderTooltipContainer()}
                </div>
                <div className='days-container'>
                    <div className='day no-select'>
                        <span className='day-label'>Ma</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>Di</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>Wo</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>Do</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>Vr</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>Za</span>
                    </div>
                    <div className='day no-select'>
                        <span className='day-label'>Zo</span>
                    </div>
                    {padBeforeDays.map((day: number, index: number) => {
                        return renderDay(day, index, onSelectPadDay(day, 'prev'), 'pad', 'prev');
                    })}
                    {days.map((day: number, index: number) => {
                        const isSelected = day === pickerValue.day();
                        return renderDay(day, index, onSelectDay(day),
                            classNames(isSelected && 'selected', day === 0 && 'hide'));
                    })}
                    {padAfterDays.map((day: number, index: number) => {
                        return renderDay(day, index, onSelectPadDay(day, 'next'), 'pad', 'next');
                    })}
                </div>
                <div className='actions-container'>
                    <DvrdButton onClick={onClickToday} label='Vandaag' className='action'/>
                    <DvrdButton onClick={onClose} label='Sluiten' secondary className='action'/>
                </div>
            </div>
        </WithBackground>
    )
}

interface TimePickerProps {
    onChange: ChangeFunction<IDate>;
    onClose: VoidFunction;
    value: IDate | null;
    open: boolean;
    timeMode: DatePickerTimeMode;
    closeOnChange?: boolean;
}

function TimePicker(props: TimePickerProps) {
    const {onChange, value, open, onClose, timeMode, closeOnChange} = props;
    const internalValue = useRef<IDate>(value ?? new IDate('00:00:00', 'HH:mm:ss'));

    function onClickNow() {
        internalValue.current = IDate.now();
        if (closeOnChange)
            onSubmit();
    }

    function onSlideChange(key: 'hour' | 'minute' | 'second') {
        return function (swiper: SwiperClass) {
            if (key === 'hour') internalValue.current = internalValue.current.hours(swiper.realIndex);
            if (key === 'minute') internalValue.current = internalValue.current.minutes(swiper.realIndex);
            if (key === 'second') internalValue.current = internalValue.current.seconds(swiper.realIndex);
        }
    }

    function onSubmit() {
        onChange(internalValue.current.clone());
        onClose();
    }

    function renderTimeContainer() {
        const renderHours = [DatePickerTimeMode.FULL, DatePickerTimeMode.HOURS_MINUTES, DatePickerTimeMode.HOURS_ONLY].includes(timeMode);
        const renderMinutes = [DatePickerTimeMode.FULL, DatePickerTimeMode.HOURS_MINUTES, DatePickerTimeMode.MINUTES_ONLY].includes(timeMode);
        const renderSeconds = [DatePickerTimeMode.FULL, DatePickerTimeMode.SECONDS_ONLY].includes(timeMode);
        const timeControls: Array<React.ReactElement> = [];
        if (renderHours) timeControls.push(renderTimeSwiper('hour'));
        if (renderMinutes) timeControls.push(renderTimeSwiper('minute'));
        if (renderSeconds) timeControls.push(renderTimeSwiper('second'));
        return (
            <div className='time-swiper-container'>
                <div className='vizor top'/>
                {timeControls.map((control: React.ReactElement, index: number) => {
                    return (
                        <Fragment key={index}>
                            {control}
                            {index < timeControls.length - 1 && <span className='sep'>:</span>}
                        </Fragment>
                    )
                })}
                <div className='vizor bottom'/>
            </div>
        )
    }

    function renderTimeSwiper(key: 'hour' | 'minute' | 'second') {
        const _value = value ?? new IDate('00:00:00', 'HH:mm:ss');
        let timeValue: number, numSlides: number;
        if (key === 'hour') {
            timeValue = _value.hours();
            numSlides = 24;
        } else if (key === 'minute') {
            timeValue = _value.minutes();
            numSlides = 60;
        } else {
            timeValue = _value.seconds();
            numSlides = 60;
        }
        const slideValues: Array<string> = [];
        for (let i = 0; i < numSlides; i++) slideValues.push(pad(i));
        return (
            <Swiper modules={[Mousewheel, FreeMode]} slidesPerView={3} direction='vertical' initialSlide={timeValue}
                    onTransitionEnd={onSlideChange(key)} mousewheel={{enabled: true, sensitivity: .5}} freeMode={{
                enabled: true,
                sticky: true,
                minimumVelocity: .1,
                momentumVelocityRatio: .25,
                momentumRatio: .25
            }} loop slideToClickedSlide centeredSlides>
                {slideValues.map((value: string, index: number) => <SwiperSlide key={index}>{value}</SwiperSlide>)}
            </Swiper>
        )
    }

    useEffect(() => {
        if (value) internalValue.current = value;
    }, [value]);

    return (
        <WithBackground active={open} onClose={onClose}>
            <div className='picker time'>
                <div className='switcher'>
                    <label className='switcher-label'>Tijdstip</label>
                </div>
                {renderTimeContainer()}
                <div className='actions-container'>
                    <DvrdButton onClick={onClickNow} label='Huidig tijdstip' secondary className='action'/>
                    <DvrdButton onClick={onSubmit} label='Oké' className='action'/>
                </div>
            </div>
        </WithBackground>
    );
}

export default forwardRef<DVRDDatePickerRef, Props>(DvrdDatePicker);